import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Search } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LinkProfileDialog = ({ record, onClose, onLinked }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (record) { setQ(`${record.first_name || ''} ${record.last_name || ''}`.trim()); setResults([]); } }, [record]);
  if (!record) return null;

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/participants`, { params: { search: q.trim(), limit: 20 } });
      setResults(data.items || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const link = async (p) => {
    try {
      await axios.put(`${API}/referral-tracker/records/${record.id}/link`, { participant_id: p.id });
      toast.success(`Linked to ${p.full_name}`);
      onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not link');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rt-link-dialog">
        <DialogHeader>
          <DialogTitle>Link to profile</DialogTitle>
          <DialogDescription>Find the existing MyHELP profile for {record.first_name} {record.last_name}{record.date_of_birth ? ` (DOB ${formatDate(record.date_of_birth)})` : ''}.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Name, email or last 4 of SSN" data-testid="rt-link-search-input" />
          <Button onClick={search} disabled={loading} data-testid="rt-link-search-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y" data-testid="rt-link-results">
          {results.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium">{p.full_name}</p>
                <p className="text-xs text-muted-foreground">DOB {formatDate(p.date_of_birth) || '—'} · {p.email || 'no email'} · SSN ***-**-{p.ssn_last_four || '????'}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => link(p)} data-testid={`rt-link-pick-${p.id}`}>Link</Button>
            </div>
          ))}
          {!loading && !results.length && <p className="text-xs text-muted-foreground py-4 text-center">Search to see matching profiles.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="rt-link-cancel">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
